/**
 * Single Learn More page — /learn-more
 */
import React from "react";
import TopNavbar from "../../shares/TopNavbar";
import {
    LearnMoreFooter,
    FORA_SIGNUP_URL,
    QuickBooksTrademarkSection,
} from "./shared";

export default function LearnMoreIndex() {
    return (
        <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-100 text-gray-900">
            <TopNavbar />
            <article className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
                <header className="text-center mb-12">
                    <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        Fora Connector for QuickBooks workflows
                    </h1>
                    <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                        Sync customers, products, and sales orders between <strong>QuickBooks Online</strong>,{" "}
                        <strong>QuickBooks Desktop</strong>, and the <strong>Fora Mobile Commerce Platform</strong>
                        with a connector designed for operational teams.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
                        <a
                            href={FORA_SIGNUP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-8 py-3.5 text-base font-bold text-white shadow-lg shadow-slate-900/20 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2"
                        >
                            Start my free trial
                        </a>
                    </div>
                </header>

                <section className="rounded-2xl bg-white p-8 sm:p-10 shadow-md ring-1 ring-slate-200/80 mb-8">
                    <h2 className="text-2xl font-bold text-slate-900 mb-4">What the connector does</h2>
                    <p className="text-slate-700 leading-relaxed mb-6">
                        Fora Connector moves customer, product, and sales-order data between the Fora platform and the
                        accounting workflow you select. Teams use it to reduce manual re-entry, keep product catalogs
                        aligned, and review transfer results before they affect day-to-day operations.
                    </p>
                    <ul className="space-y-4 text-slate-700 leading-relaxed list-none pl-0">
                        <li className="flex gap-3">
                            <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">
                                1
                            </span>
                            <span>
                                <strong>QuickBooks Online</strong> connections use Intuit&apos;s OAuth flow so an
                                authorized user can securely approve access and return to Fora Connector.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">
                                2
                            </span>
                            <span>
                                <strong>QuickBooks Desktop</strong> workflows use the Fora desktop helper on Windows to
                                support local company-file integrations and ongoing synchronization.
                            </span>
                        </li>
                        <li className="flex gap-3">
                            <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-bold text-slate-700">
                                3
                            </span>
                            <span>
                                Mapping, job controls, and logs are built into the connector so your team can review how
                                fields align and confirm each run completed successfully.
                            </span>
                        </li>
                    </ul>
                    <div className="mt-8 pt-8 border-t border-slate-200">
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="/learn-more/quickbooks-online"
                                className="inline-flex items-center justify-center rounded-lg border-2 border-slate-900 bg-white px-6 py-3 text-sm font-bold text-slate-900 hover:bg-slate-50"
                            >
                                QuickBooks Online details
                            </a>
                            <a
                                href="/learn-more/quickbooks-desktop"
                                className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-6 py-3 text-sm font-bold text-slate-700 hover:border-slate-400 hover:text-slate-900"
                            >
                                QuickBooks Desktop details
                            </a>
                        </div>
                    </div>
                </section>

                <section className="rounded-2xl bg-slate-900 p-8 sm:p-10 text-white mb-8">
                    <h2 className="text-2xl font-bold mb-4">What reviewers and buyers should expect</h2>
                    <p className="text-slate-300 leading-relaxed mb-6">
                        Fora Connector includes connection, mapping, job execution, and log review in one workflow so
                        administrators can see what data is moving and when.
                    </p>
                    <p className="text-slate-300 leading-relaxed">
                        Published support, pricing, privacy, and terms information is available below so reviewers can
                        validate the service model before publication. The connector describes interoperability with
                        QuickBooks products without implying endorsement by Intuit.
                    </p>
                    <div className="mt-8">
                        <a
                            href="/"
                            className="inline-flex rounded-lg bg-white px-5 py-2.5 text-sm font-bold text-slate-900 hover:bg-slate-100"
                        >
                            Open Fora Connector dashboard
                        </a>
                    </div>
                </section>

                <QuickBooksTrademarkSection />
                <LearnMoreFooter />
            </article>
        </div>
    );
}
